import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import MarkdownEditor from "../../../components/Markdown/MarkdownEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faTimes } from "@fortawesome/free-solid-svg-icons";

import axios from "../../../public Func/axiosAuth";
import "./Post.css";

function PostEditModal({ post, onClose, onSaved }) {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploadingAttachedImage, setUploadingAttachedImage] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const [formData, setFormData] = useState({
    communityID: post.communityID ? post.communityID.toString() : "",
    mainText: post.mainText || "",
    date: (post.date || new Date().toISOString()).split("T")[0],
    hideIdentity: !!post.hideIdentity,
    attachedImages:
      post.images && Array.isArray(post.images)
        ? post.images.map((img) => ({ name: img.name, link: img.link }))
        : [],
  });

  useEffect(() => {
    const fetchCommunities = async () => {
      try {
        const res = await axios.get(
          process.env.REACT_APP_BACKEND_URL + "/community/community-list",
        );
        const list = res.data || [];
        setCommunities(list);
        setFormData((prev) => ({
          ...prev,
          communityID: prev.communityID || (list.length > 0 ? list[0].id.toString() : ""),
        }));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching communities:", err);
        setMessage({ type: "error", text: "Failed to load communities. Please try again." });
        setLoading(false);
      }
    };
    fetchCommunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setMessage({ type: "", text: "" });
  };

  // MarkdownEditor reports its contents as a Markdown string rather than a DOM
  // event, so it cannot go through handleInputChange, which reads name/value off
  // e.target and throws on a plain string.
  const handleMarkdownChange = (value) => {
    setFormData((prev) => ({ ...prev, mainText: value }));
    setMessage({ type: "", text: "" });
  };

  const handleAttachedImageAdd = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Please select an image file." });
      return;
    }

    try {
      setUploadingAttachedImage(true);
      setMessage({ type: "", text: "" });

      const uploadData = new FormData();
      uploadData.append("file", file);

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/upload",
        uploadData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const imageUrl =
        typeof res.data === "string"
          ? res.data
          : res.data?.link || res.data?.file || res.data?.filename || res.data;

      setFormData((prev) => ({
        ...prev,
        attachedImages: [
          ...prev.attachedImages,
          { name: file.name, link: imageUrl },
        ],
      }));
    } catch (err) {
      console.error("Error uploading image:", err);
      setMessage({
        type: "error",
        text: err.response?.data || "Failed to upload image. Please try again.",
      });
    } finally {
      setUploadingAttachedImage(false);
      e.target.value = "";
    }
  };

  const handleImageRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachedImages: prev.attachedImages.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.communityID) {
      setMessage({ type: "error", text: "Please select a community." });
      return;
    }
    if (!formData.mainText.trim()) {
      setMessage({ type: "error", text: "Please enter post content." });
      return;
    }
    if (!formData.date) {
      setMessage({ type: "error", text: "Please select a date." });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const payload = {
        postID: post.id,
        communityID: parseInt(formData.communityID),
        mainText: formData.mainText.trim(),
        date: new Date(formData.date).toISOString(),
        hideIdentity: formData.hideIdentity,
        attachedImages:
          formData.attachedImages.length > 0 ? formData.attachedImages : undefined,
      };

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/admin/edit-post",
        payload,
      );

      if (
        res.status === 200 &&
        (res.data === "Done" || res.data === "done" || res.data?.done)
      ) {
        const community =
          communities.find(
            (c) => c.id.toString() === formData.communityID.toString(),
          )?.name || post.community;
        onSaved({
          mainText: payload.mainText,
          community,
          communityID: payload.communityID,
          date: payload.date,
          hideIdentity: payload.hideIdentity,
          edited: true,
          images: formData.attachedImages,
        });
        onClose();
      } else {
        setMessage({
          type: "error",
          text: res.data || "Failed to edit post. Please try again.",
        });
      }
    } catch (err) {
      console.error("Error editing post:", err);
      setMessage({
        type: "error",
        text: err.response?.data || "Failed to edit post. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="ReportPopupWindow">
      <div className="backgroundBlock" onClick={onClose}></div>
      <div className="ReportForm edit-form-modal">
        <h1 className="TitleReport">Edit Post</h1>
        {loading ? (
          <p className="GrayText">Loading communities...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="edit-form-group">
              <label>Community *</label>
              <select
                name="communityID"
                value={formData.communityID}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="edit-form-group">
              <label>Post Content *</label>
              <MarkdownEditor
                value={formData.mainText}
                onChange={handleMarkdownChange}
                rows={8}
                ariaLabel="Post Content"
                placeholder="Write your post content here..."
              />
            </div>

            <div className="edit-form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="edit-form-group edit-checkbox-group">
              <label className="edit-checkbox-label">
                <input
                  type="checkbox"
                  name="hideIdentity"
                  checked={formData.hideIdentity}
                  onChange={handleInputChange}
                />
                <span>Hide Identity</span>
              </label>
            </div>

            <div className="edit-form-group">
              <label>Attached Images</label>
              <div className="images-section">
                {formData.attachedImages.map((image, index) => (
                  <div
                    key={index}
                    className="image-preview-container attached-image-preview"
                  >
                    <div className="image-preview-wrapper">
                      <img
                        src={image.link}
                        alt={image.name}
                        className="image-preview attached-image"
                        onClick={() => setModalImage(image.link)}
                        style={{ cursor: "pointer" }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageRemove(index);
                        }}
                        style={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                ))}
                <input
                  id="editAttachedImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleAttachedImageAdd}
                  disabled={uploadingAttachedImage}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="editAttachedImageInput"
                  className="add-image-btn"
                  style={{
                    cursor: uploadingAttachedImage ? "not-allowed" : "pointer",
                    opacity: uploadingAttachedImage ? 0.6 : 1,
                  }}
                >
                  <FontAwesomeIcon icon={faImage} style={{ marginRight: "0.5rem" }} />
                  {uploadingAttachedImage ? "Uploading..." : "Add Image"}
                </label>
              </div>
            </div>

            {message.text && (
              <div className={`message ${message.type}`}>{message.text}</div>
            )}

            <div className="edit-form-actions">
              <button
                type="button"
                className="submutReportButton edit-cancel-btn"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submutReportButton"
                disabled={submitting}
                style={{ background: "var(--primary-500)" }}
              >
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>

      {modalImage && (
        <div
          className="image-modal-overlay"
          onClick={() => setModalImage(null)}
        >
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={() => setModalImage(null)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <img
              src={modalImage}
              alt="Full size preview"
              className="image-modal-image"
            />
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

export default PostEditModal;